import React, { useCallback, useState } from 'react';

import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios';
import { isEmpty, noop } from 'lodash';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import FileRecord from './FileRecord';

import { DropZoneLabel } from '~/core/molecules/FileUpload/DropZoneLabel';
import { FileUploadResponse, UploadingFile } from '~/core/molecules/FileUpload/types';
import cn from '~/utils/cn';

export interface FileUploadProps {
  uploadUrl: string;
  onUploadSuccess?: (response: FileUploadResponse, file: File) => void;
  onUploadError?: (error: unknown, file: File) => void;
  onFileRemove?: (file: File) => void;
  label?: React.ReactNode;
  multiple?: boolean;
  headers?: Record<string, string>;
}

const FileUpload: React.FC<FileUploadProps> = ({
  uploadUrl,
  onUploadSuccess = noop,
  onUploadError = noop,
  onFileRemove,
  label,
  multiple = true,
  headers = {},
}) => {
  const [files, setFiles] = useState<UploadingFile[]>([]);

  const onUploadProgress = React.useCallback((event: AxiosProgressEvent, file: File) => {
    if (event.total) {
      const percent = Math.round((event.loaded * 100) / event.total);
      setFiles((prev) =>
        prev.map((uploadingFile) =>
          uploadingFile.file === file
            ? {
                ...uploadingFile,
                progress: percent,
              }
            : uploadingFile
        )
      );
    }
  }, []);

  const onUploadCompleted = React.useCallback(
    ({ data, file }: { data: FileUploadResponse; file: File }) => {
      const error = data?.errors?.[0]?.error_message ?? null;

      setFiles((prev) =>
        prev.map((uploadingFile) =>
          uploadingFile.file === file
            ? {
                ...uploadingFile,
                progress: 100,
                status: error ? 'error' : 'success',
                error,
              }
            : uploadingFile
        )
      );

      if (error) {
        onUploadError(error, file);
        return;
      }

      onUploadSuccess(data, file);
    },
    [onUploadError, onUploadSuccess]
  );

  const uploadFile = React.useCallback(
    async (file: File, cancelTokenSource: CancelTokenSource) => {
      const formData = new FormData();
      formData.append('files', file);
      try {
        const { data } = await axios.post<FileUploadResponse>(uploadUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...headers,
          },
          onUploadProgress: (event: AxiosProgressEvent) => onUploadProgress(event, file),
          cancelToken: cancelTokenSource.token,
        });
        onUploadCompleted({ data, file });
      } catch (error: unknown) {
        const { message } = error as { message: string };
        setFiles((prev) =>
          prev.map((uploadingFile) =>
            uploadingFile.file === file
              ? {
                  ...uploadingFile,
                  status: 'error',
                  error: message,
                }
              : uploadingFile
          )
        );
        onUploadError(error, file);
      }
    },
    [uploadUrl, headers, onUploadError, onUploadProgress, onUploadCompleted]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        const cancelTokenSource = axios.CancelToken.source();
        const uploadingFile: UploadingFile = {
          id: uuidv4(), // Ensure you have uuidv4 imported from 'uuid'
          file,
          progress: 0,
          status: 'uploading',
          cancelTokenSource,
        };
        setFiles((prev) => [...prev, uploadingFile]);
        uploadFile(file, cancelTokenSource);
      });
    },
    [uploadFile]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple,
  });

  const handleRemove = (file: File) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((uploadingFile) => uploadingFile.file === file);
      if (fileToRemove && fileToRemove.status === 'uploading') {
        fileToRemove.cancelTokenSource.cancel('Upload cancelled by user');
      }
      return prev.filter((uploadingFile) => uploadingFile.file !== file);
    });
    onFileRemove?.(file);
  };

  return (
    <div>
      <div
        {...getRootProps()}
        className={cn('border-grey-300 mb-12 cursor-pointer rounded-lg border-2 border-dashed p-8 text-center', {
          'bg-bluesky-100 animate-pulse': isDragActive,
        })}
      >
        <input {...getInputProps()} />
        <div onClick={open} className="cursor-pointer">
          {label ? label : <DropZoneLabel isDragActive={isDragActive} onClick={open} />}
        </div>
      </div>
      {!isEmpty(files) && (
        <div className="flex flex-col gap-8">
          <div className="flex w-full items-center justify-center text-base font-semibold">Uploaded Files</div>
          <div className="flex max-h-60 flex-col gap-8 overflow-auto rounded-md">
            {files.map((uploadingFile) => (
              <FileRecord key={uploadingFile.id} file={uploadingFile} onRemove={handleRemove} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
